/* eslint-disable max-len */
import { useState, useEffect } from 'react';

/**
 * This hook calculates the luminance of a certain area in the image and returns the
 * appropriate text color based on the threshold.
 */

const useLuminance = (
  imageUrl: string | undefined,
  x: number = 0.9,
  y: number = 0.9,
  threshold = 0.5,
  shouldCalculate = false, // New parameter to control when to calculate
  imgElement?: HTMLImageElement | null // Optional image element to use directly and avoid a new Image creation and loading
) => {
  const [textColor, setTextColor] = useState<string>('white');

  const calculateLuminance = (r: number, g: number, b: number) => 
    (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  const drawAndCalculate = (image: HTMLImageElement, width: number, height: number) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;

    ctx?.drawImage(image, 0, 0);

    const sampleX = Math.floor(width * x);
    const sampleY = Math.floor(height * y);
    try {
      const [r, g, b] = ctx?.getImageData(sampleX, sampleY, 1, 1).data || [0, 0, 0];
      const luminance = calculateLuminance(r, g, b);
      setTextColor(luminance > threshold ? '#000000' : '#ffffff');
    } catch (e) {
      setTextColor('#ffffff');
      if (process.env.NODE_ENV !== 'production') {
        console.warn('useLuminance: could not get image data, using fallback color.', e);
      }
    }
    canvas.remove();
  };

  useEffect(() => {
    if (!shouldCalculate) return;

    // If an image element is provided and loaded, use it directly
    if (imgElement && imgElement.complete && imgElement.naturalWidth > 0) {
      drawAndCalculate(imgElement, imgElement.naturalWidth, imgElement.naturalHeight);
      return;
    }

    // Fallback: create a new image if no element is provided
    if (!imageUrl) return;
    const img = new Image();

    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;

    img.onload = () => {
      drawAndCalculate(img, img.width, img.height);
    };

    return () => {
      img.onload = null;
    };
  }, [imageUrl, x, y, threshold, shouldCalculate, imgElement]);

  return textColor;
};

export default useLuminance;
